import React, { useState, useRef, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown, ShoppingBag, ArrowRight } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { api } from "../lib/api";
import { useCart } from "../contexts/CartContext";
import LanguageSwitcher, { LOGO_URL } from "./LanguageSwitcher";

const FALLBACK_LINKS = [
  { to: "/le-domaine", label: "Le Domaine", children: [] },
  { to: "/hebergements", label: "Hébergements", children: [] },
  { to: "/mariages", label: "Mariages", children: [] },
  { to: "/seminaires", label: "Séminaires", children: [] },
  { to: "/haras", label: "Le Haras", children: [] },
  { to: "/tourisme", label: "Tourisme", children: [] },
  { to: "/nous-contacter", label: "Contact", children: [] },
];

const HEADER_MENU_CACHE_KEY_BASE = "briouse:cms:menu:header:v3";

// EP — Dropdown « Événements & Privatisations » orienté par intention (aligné landing /evenements-prives)
const EP_PATH = "/evenements-prives";
const EP_UNIVERS = [
  { id: "celebrer", over: "Célébrer", label: "Célébrations & événements privés" },
  { id: "recevoir", over: "Recevoir", label: "Réceptions & privatisations" },
  { id: "tourner", over: "Tourner", label: "Tournages & shootings" },
  { id: "creer", over: "Créer", label: "Résidences & projets artistiques" },
];

function buildTree(rawItems) {
  const nodes = rawItems.map((i) => {
    const isExternal = i.target_type === "external" || i.target_type === "url";
    const basePath = isExternal ? i.external_url : i.page_path;
    const to = basePath ? (i.anchor ? `${basePath}#${i.anchor}` : basePath) : null;
    return {
      rawId: i.id,
      parentRawId: i.parent_id || null,
      orderIndex: i.order_index ?? 0,
      to,
      label: i.label,
      external: isExternal && /^https?:/.test(i.external_url || ""),
      id: `nav-${(i.id || "").slice(0, 8)}`,
      children: [],
    };
  }).filter((n) => !!n.to);

  const byRawId = new Map(nodes.map((n) => [n.rawId, n]));
  const roots = [];
  for (const n of nodes) {
    if (n.parentRawId && byRawId.has(n.parentRawId)) {
      byRawId.get(n.parentRawId).children.push(n);
    } else {
      roots.push(n);
    }
  }
  const sortRec = (arr) => {
    arr.sort((a, b) => a.orderIndex - b.orderIndex);
    arr.forEach((n) => sortRec(n.children));
  };
  sortRec(roots);
  return roots;
}

function useHeaderMenu() {
  const { locale } = useI18n();
  const cacheKey = `${HEADER_MENU_CACHE_KEY_BASE}:${locale}`;
  const [items, setItems] = useState(() => {
    try {
      const cached = sessionStorage.getItem(cacheKey);
      return cached ? JSON.parse(cached) : [];
    } catch { return []; }
  });
  useEffect(() => {
    let cancelled = false;
    // Hydrate depuis le cache pour la locale courante
    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) setItems(JSON.parse(cached));
    } catch { /* ignore */ }
    api.get(`/public/cms/menu/header?locale=${encodeURIComponent(locale)}`)
      .then(({ data }) => {
        if (cancelled) return;
        const rawVisible = (data?.items || []).filter((i) => i.is_visible !== false);
        const tree = buildTree(rawVisible);
        const next = tree.length > 0 ? tree : FALLBACK_LINKS;
        setItems(next);
        try { sessionStorage.setItem(cacheKey, JSON.stringify(next)); } catch { /* ignore */ }
      })
      .catch(() => setItems((prev) => prev.length > 0 ? prev : FALLBACK_LINKS));
    return () => { cancelled = true; };
  }, [locale, cacheKey]);
  return items;
}

function DesktopNavItem({ item, align = "center" }) {
  const hasChildren = item.children && item.children.length > 0;
  // Détecte si au moins un enfant a lui-même des enfants → mega-menu multi-colonnes
  const isMega = hasChildren && item.children.some((c) => c.children && c.children.length > 0);
  const alignClass =
    align === "left" ? "left-0"
      : align === "right" ? "right-0"
      : "left-1/2 -translate-x-1/2";
  return (
    <div className="relative group" data-testid={`${item.id}-container`}>
      <NavLink
        to={item.to}
        data-testid={`${item.id}-link`}
        className={({ isActive }) =>
          `relative flex items-center gap-1 transition-colors duration-200 px-2 lg:px-2.5 py-1.5 whitespace-nowrap ${isActive
            ? "text-domaine font-medium"
            : "text-ardoise/80 group-hover:text-domaine"}`
        }
      >
        {({ isActive }) => (
          <>
            <span>{item.label}</span>
            {hasChildren && <ChevronDown size={12} strokeWidth={2} className="opacity-60 transition-transform duration-200 group-hover:rotate-180" />}
            <span aria-hidden="true"
                  className={`absolute bottom-0 left-2 right-2 h-px bg-or origin-left transition-transform duration-200 ${isActive ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"}`} />
          </>
        )}
      </NavLink>
      {hasChildren && !isMega && (
        <div
          className={`absolute ${alignClass} top-full pt-2 min-w-[220px] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-150 z-50`}
          data-testid={`${item.id}-submenu`}
        >
          <div className="bg-lin border border-pierre shadow-xl py-2">
            {item.children.map((c) => (
              <NavLink
                key={c.id}
                to={c.to}
                data-testid={`${c.id}-sublink`}
                className={({ isActive }) =>
                  `block px-4 py-2 text-[11px] tracking-[0.15em] uppercase transition-colors duration-150 ${isActive
                    ? "text-domaine font-medium bg-domaine/5"
                    : "text-ardoise/80 hover:text-domaine hover:bg-domaine/5"}`
                }
              >
                {c.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
      {hasChildren && isMega && (
        <div
          className="fixed left-0 right-0 top-auto pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-150 z-50"
          data-testid={`${item.id}-megamenu`}
        >
          <div className="mx-auto max-w-7xl bg-lin border border-pierre shadow-lg">
            <div
              className="grid divide-x divide-pierre/60"
              style={{ gridTemplateColumns: `repeat(${item.children.length}, minmax(0, 1fr))` }}
            >
              {item.children.map((col) => (
                <div key={col.id} className="p-5">
                  <NavLink
                    to={col.to}
                    data-testid={`${col.id}-sublink`}
                    className={({ isActive }) =>
                      `block text-[12px] tracking-[0.18em] uppercase font-medium mb-3 pb-2 border-b border-pierre transition-colors ${isActive
                        ? "text-domaine"
                        : "text-domaine hover:opacity-70"}`
                    }
                  >
                    {col.label}
                  </NavLink>
                  {col.children && col.children.length > 0 ? (
                    <div className="flex flex-col gap-1">
                      {col.children.map((leaf) => (
                        <NavLink
                          key={leaf.id}
                          to={leaf.to}
                          data-testid={`${leaf.id}-sublink`}
                          className={({ isActive }) =>
                            `text-[11px] tracking-wide normal-case px-2 py-1.5 transition-colors duration-150 ${isActive
                              ? "text-domaine font-medium bg-domaine/5"
                              : "text-ardoise/80 hover:text-domaine hover:bg-domaine/5"}`
                          }
                        >
                          {leaf.label}
                        </NavLink>
                      ))}
                    </div>
                  ) : (
                    <span className="text-[10px] text-ardoise/50 italic normal-case">Voir la page</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EventsNavItem({ item, align = "center" }) {
  const alignClass =
    align === "left" ? "left-0"
      : align === "right" ? "right-0"
      : "left-1/2 -translate-x-1/2";
  return (
    <div className="relative group" data-testid="ep-nav-container">
      <NavLink
        to={EP_PATH}
        data-testid="ep-nav-link"
        className={({ isActive }) =>
          `relative flex items-center gap-1 transition-colors duration-200 px-2 lg:px-2.5 py-1.5 whitespace-nowrap ${isActive
            ? "text-domaine font-medium"
            : "text-ardoise/80 group-hover:text-domaine"}`
        }
      >
        {({ isActive }) => (
          <>
            <span>{item.label}</span>
            <ChevronDown size={12} strokeWidth={2} className="opacity-60 transition-transform duration-200 group-hover:rotate-180" />
            <span aria-hidden="true"
                  className={`absolute bottom-0 left-2 right-2 h-px bg-or origin-left transition-transform duration-200 ${isActive ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"}`} />
          </>
        )}
      </NavLink>
      <div
        className={`absolute ${alignClass} top-full pt-2 w-[320px] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-150 z-50`}
        data-testid="ep-nav-submenu"
      >
        <div className="bg-lin border border-pierre shadow-xl py-2">
          {EP_UNIVERS.map((u) => (
            <Link
              key={u.id}
              to={`${EP_PATH}#${u.id}`}
              data-testid={`ep-nav-${u.id}`}
              className="block px-5 py-2.5 transition-colors duration-150 hover:bg-domaine/5 group/ep"
            >
              <span className="block text-[10px] uppercase tracking-[0.24em] text-or">{u.over}</span>
              <span className="block mt-0.5 text-[13px] normal-case tracking-normal text-ardoise/85 group-hover/ep:text-domaine transition-colors">{u.label}</span>
            </Link>
          ))}
          <Link
            to={`${EP_PATH}#espaces`}
            data-testid="ep-nav-espaces"
            className="block px-5 py-3 mt-1 border-t border-pierre/50 text-[11px] tracking-[0.15em] uppercase text-ardoise/80 hover:text-domaine hover:bg-domaine/5 transition-colors duration-150"
          >
            Espaces &amp; hébergements
          </Link>
          <div className="mx-5 my-1 border-t border-pierre/40" aria-hidden="true" />
          <Link
            to={`${EP_PATH}#formulaire`}
            data-testid="ep-nav-projet"
            className="flex items-center gap-1.5 px-5 py-2.5 text-[11px] tracking-[0.2em] uppercase font-medium text-domaine hover:text-or transition-colors duration-150"
          >
            <span>Parler de votre projet</span>
            <ArrowRight size={13} strokeWidth={2} className="transition-transform duration-200 group-hover:translate-x-0.5" />
          </Link>
        </div>
      </div>
    </div>
  );
}

function MobileLeafLink({ item, onNavigate, textClass = "" }) {
  return (
    <NavLink
      to={item.to}
      onClick={onNavigate}
      data-testid={`mobile-${item.id}-link`}
      className={({ isActive }) =>
        `flex-1 px-3 py-2 transition-colors ${textClass} ${isActive
          ? "text-domaine font-medium border-l-2 border-or -ml-px"
          : "text-ardoise/80 hover:text-domaine"}`
      }
    >
      {item.label}
    </NavLink>
  );
}

function MobileNavItem({ item, onNavigate }) {
  const [expanded, setExpanded] = useState(false);
  const [expandedChild, setExpandedChild] = useState(null);
  const hasChildren = item.children && item.children.length > 0;
  return (
    <div className="flex flex-col">
      <div className="flex items-stretch">
        <MobileLeafLink item={item} onNavigate={onNavigate} />
        {hasChildren && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-label={expanded ? "Fermer" : "Ouvrir"}
            data-testid={`mobile-${item.id}-toggle`}
            className="px-3 text-ardoise hover:bg-domaine hover:text-lin transition-colors"
          >
            <ChevronDown size={16} className={`transition-transform ${expanded ? "rotate-180" : ""}`} />
          </button>
        )}
      </div>
      {hasChildren && expanded && (
        <div className="flex flex-col pl-4 border-l border-pierre ml-3 mt-1 mb-2">
          {item.children.map((child) => {
            const grand = child.children && child.children.length > 0;
            const isOpen = expandedChild === child.id;
            return (
              <div key={child.id} className="flex flex-col">
                <div className="flex items-stretch">
                  <MobileLeafLink item={child} onNavigate={onNavigate} textClass="text-xs" />
                  {grand && (
                    <button
                      type="button"
                      onClick={() => setExpandedChild(isOpen ? null : child.id)}
                      aria-label={isOpen ? "Fermer" : "Ouvrir"}
                      className="px-3 text-ardoise hover:bg-domaine hover:text-lin transition-colors"
                    >
                      <ChevronDown size={14} className={`transition-transform ${isOpen ? "rotate-180" : ""}`} />
                    </button>
                  )}
                </div>
                {grand && isOpen && (
                  <div className="flex flex-col pl-4 border-l border-pierre ml-3 mt-1 mb-1">
                    {child.children.map((leaf) => (
                      <MobileLeafLink key={leaf.id} item={leaf} onNavigate={onNavigate} textClass="text-xs" />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function EventsMobileNavItem({ item, onNavigate }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="flex flex-col">
      <div className="flex items-stretch">
        <NavLink
          to={EP_PATH}
          onClick={onNavigate}
          data-testid="mobile-ep-nav-link"
          className={({ isActive }) =>
            `flex-1 px-3 py-2 transition-colors ${isActive
              ? "text-domaine font-medium border-l-2 border-or -ml-px"
              : "text-ardoise/80 hover:text-domaine"}`
          }
        >
          {item.label}
        </NavLink>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-label={expanded ? "Fermer" : "Ouvrir"}
          data-testid="mobile-ep-nav-toggle"
          className="px-3 text-ardoise hover:bg-domaine hover:text-lin transition-colors"
        >
          <ChevronDown size={16} className={`transition-transform ${expanded ? "rotate-180" : ""}`} />
        </button>
      </div>
      {expanded && (
        <div className="flex flex-col pl-4 border-l border-pierre ml-3 mt-1 mb-2">
          {EP_UNIVERS.map((u) => (
            <Link
              key={u.id}
              to={`${EP_PATH}#${u.id}`}
              onClick={onNavigate}
              data-testid={`mobile-ep-nav-${u.id}`}
              className="px-3 py-2 text-ardoise/85 hover:text-domaine transition-colors"
            >
              <span className="block text-[10px] uppercase tracking-[0.2em] text-or">{u.over}</span>
              <span className="block mt-0.5 text-xs normal-case tracking-normal">{u.label}</span>
            </Link>
          ))}
          <Link
            to={`${EP_PATH}#espaces`}
            onClick={onNavigate}
            data-testid="mobile-ep-nav-espaces"
            className="px-3 py-2 mt-1 border-t border-pierre/50 text-[11px] uppercase tracking-[0.12em] text-ardoise/80 hover:text-domaine transition-colors"
          >
            Espaces &amp; hébergements
          </Link>
          <Link
            to={`${EP_PATH}#formulaire`}
            onClick={onNavigate}
            data-testid="mobile-ep-nav-projet"
            className="flex items-center gap-1.5 px-3 py-2 mt-1 text-[11px] uppercase tracking-[0.16em] font-medium text-domaine hover:text-or transition-colors"
          >
            <span>Parler de votre projet</span>
            <ArrowRight size={13} strokeWidth={2} />
          </Link>
        </div>
      )}
    </div>
  );
}

export default function Header() {
  const { t, locale } = useI18n();
  const en = locale === "en";
  const [open, setOpen] = useState(false);
  const items = useHeaderMenu();
  const nav = useNavigate();
  const clicksRef = useRef({ count: 0, first: 0 });
  const { totalItems, setDrawerOpen } = useCart();

  const handleLogoClick = (e) => {
    const now = Date.now();
    const c = clicksRef.current;
    if (now - c.first > 3000) { c.count = 1; c.first = now; }
    else c.count += 1;
    if (c.count >= 3) {
      e.preventDefault();
      c.count = 0;
      c.first = 0;
      nav("/emergency-access");
    }
  };

  return (
    <header
      className="sticky top-0 z-50 bg-lin/90 backdrop-blur-xl border-b border-pierre/60"
      data-testid="site-header"
    >
      {/* Ligne 1 : logo à gauche + boutons à droite */}
      <div className="container-editorial flex items-center justify-between py-2.5 md:py-3">
        <Link
          to="/" data-testid="header-logo-link"
          onClick={handleLogoClick}
          className="flex-shrink-0 flex items-center gap-3 select-none"
          title="Retour à l'accueil (3 clics rapides = accès de secours)"
        >
          <img src={LOGO_URL} alt="Domaine de la Briouse" width="352" height="128" className="h-12 md:h-14 w-auto" draggable="false" />
        </Link>

        {/* Actions droite : recherche + panier + langue + login (desktop) */}
        <div className="hidden lg:flex items-center gap-4">
          <LanguageSwitcher />
          <span className="w-px h-4 bg-pierre" aria-hidden="true" />
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="relative text-ardoise hover:text-domaine transition-colors p-1"
            aria-label="Ouvrir le panier"
            data-testid="header-cart-button"
          >
            <ShoppingBag size={18} strokeWidth={1.5} />
            {totalItems > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 bg-domaine text-lin text-[9px] font-bold rounded-full inline-flex items-center justify-center"
                    data-testid="header-cart-count">
                {totalItems > 99 ? "99+" : totalItems}
              </span>
            )}
          </button>
          <Link
            to="/reserver"
            data-testid="header-book-cta"
            className="text-[11px] uppercase tracking-widest px-3 py-1.5 bg-domaine text-lin hover:bg-or hover:text-domaine transition-colors"
          >
            {en ? "Book" : "Réserver"}
          </Link>
          <Link
            to="/mon-evenement/login"
            data-testid="header-extranet-link"
            className="text-[11px] uppercase tracking-widest text-ardoise hover:text-domaine transition-colors"
          >
            {en ? "My event" : "Mon évènement"}
          </Link>
        </div>

        <div className="flex items-center gap-2 lg:hidden">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="relative text-ardoise p-2"
            aria-label="Ouvrir le panier"
            data-testid="mobile-cart-button"
          >
            <ShoppingBag size={20} strokeWidth={1.5} />
            {totalItems > 0 && (
              <span className="absolute top-0 right-0 min-w-[16px] h-4 px-1 bg-domaine text-lin text-[9px] font-bold rounded-full inline-flex items-center justify-center">
                {totalItems > 99 ? "99+" : totalItems}
              </span>
            )}
          </button>
          <button
            type="button"
            className="p-2 -mr-2 text-ardoise"
            onClick={() => setOpen((v) => !v)}
            aria-label="Ouvrir le menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Ligne 2 : menu principal centré */}
      <nav
        className="hidden lg:flex flex-wrap items-center justify-center gap-x-1 gap-y-1 lg:gap-x-2 xl:gap-x-3 2xl:gap-x-4 text-[10px] xl:text-[11px] tracking-[0.12em] xl:tracking-[0.16em] uppercase border-t border-pierre/40 py-2.5 px-2"
        aria-label="Menu principal"
      >
        {items.map((l, idx) => (
          l.to === EP_PATH ? (
            <EventsNavItem
              key={l.id}
              item={l}
              align={idx === 0 ? "left" : idx === items.length - 1 ? "right" : "center"}
            />
          ) : (
            <DesktopNavItem
              key={l.id}
              item={l}
              align={idx === 0 ? "left" : idx === items.length - 1 ? "right" : "center"}
            />
          )
        ))}
      </nav>

      {open && (
        <div className="lg:hidden border-t border-pierre bg-lin" data-testid="mobile-menu">
          <div className="container-editorial py-6 flex flex-col gap-2 text-sm uppercase tracking-widest">
            {items.map((l) => (
              l.to === EP_PATH
                ? <EventsMobileNavItem key={l.id} item={l} onNavigate={() => setOpen(false)} />
                : <MobileNavItem key={l.id} item={l} onNavigate={() => setOpen(false)} />
            ))}
            <div className="flex items-center justify-between pt-4 border-t border-pierre mt-2">
              <LanguageSwitcher />
              <div className="flex items-center gap-4">
                <Link
                  to="/reserver"
                  data-testid="mobile-book-cta"
                  onClick={() => setOpen(false)}
                  className="px-3 py-1.5 bg-domaine text-lin text-xs uppercase tracking-widest"
                >
                  {en ? "Book" : "Réserver"}
                </Link>
                <Link
                  to="/mon-evenement/login"
                  data-testid="mobile-extranet-link"
                  onClick={() => setOpen(false)}
                  className="text-domaine"
                >
                  {en ? "My event" : "Mon évènement"}
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
